import { Component, effect, input, output, signal, ChangeDetectionStrategy, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Util } from "../../../util/s25-util";

@TypeManagerDecorator("s25-ng-duration-field")
@Component({
    selector: "s25-ng-duration-field",
    standalone: true,
    imports: [FormsModule],
    template: `
        @if (readOnly()) {
            <span class="duration-readonly">{{ formatDuration() }}</span>
        } @else {
            <div class="duration-inputs">
                <label>
                    <span>Hours</span>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        class="c-input"
                        [ngModel]="durationObj().hours"
                        (ngModelChange)="onChange('hours', $event)"
                    />
                </label>
                <label>
                    <span>Minutes</span>
                    <input
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        class="c-input"
                        [ngModel]="durationObj().minutes"
                        (ngModelChange)="onChange('minutes', $event)"
                    />
                </label>
            </div>
        }
    `,
    styles: `
        .duration-inputs {
            display: flex;
            gap: 12px;
        }

        .duration-inputs label {
            display: flex;
            align-items: center;
            gap: 4px;
            font-weight: normal;
        }

        .duration-inputs label span {
            min-width: 3.5em;
            font-size: 13px;
        }

        .duration-inputs input {
            width: 70px;
        }

        .duration-readonly {
            color: #555;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DurationFieldComponent {
    readonly value = input<string>();
    readonly readOnly = input(false);
    readonly valueChange = output<string>();

    readonly durationObj = signal<{ hours: number; minutes: number }>({ hours: 0, minutes: 0 });

    private initialized = false;

    constructor() {
        effect(() => {
            const val = this.value();
            if (this.initialized) return;
            this.initialized = true;
            if (val) this.durationObj.set(DurationFieldComponent.parseDatetime(val));
        });
    }

    onChange(field: "hours" | "minutes", value: number) {
        this.durationObj.update((obj) => ({ ...obj, [field]: Math.max(0, Math.floor(value || 0)) }));
        this.valueChange.emit(DurationFieldComponent.toDatetime(this.durationObj()));
    }

    formatDuration(): string {
        const { hours, minutes } = this.durationObj();
        if (!hours && !minutes) return "None";
        return [hours ? hours + " Hrs" : null, minutes ? minutes + " Mins" : null].filter(Boolean).join(", ");
    }

    private static parseDatetime(dt: string): { hours: number; minutes: number } {
        const date = S25Util.date.parse(dt);
        return { hours: date?.getHours() || 0, minutes: date?.getMinutes() || 0 };
    }

    private static toDatetime(obj: { hours: number; minutes: number }): string {
        const date = new Date(1900, 0, 1, obj.hours, obj.minutes, 0);
        return S25Util.date.toS25ISODateTimeStrFromDate(date);
    }
}
